/**
 * DeleteQuoteDialog
 *
 * Platform-admin-only soft deletion of a quote and every booking attached to
 * it (outbound + return leg + any paired row). Rendered as a trash button on
 * the Quotes table; account users never see it.
 *
 * The whole operation is one atomic RPC — admin_soft_delete_quote — which
 * enforces the admin requirement server-side (hiding the button here is
 * cosmetic) and stamps deleted_at on the quote and its bookings. Nothing is
 * physically removed: payment history, sync logs and the conversions ledger
 * stay intact, and platform staff can restore by clearing deleted_at.
 */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Loader2, Trash2 } from 'lucide-react'

interface DeleteQuoteDialogProps {
  quoteId: string
  /** Shown in the confirmation copy so the admin knows which quote this is */
  customerName: string | null
  /** True when the quote has converted to a booking — strengthens the warning */
  isConverted: boolean
}

export function DeleteQuoteDialog({
  quoteId,
  customerName,
  isConverted,
}: DeleteQuoteDialogProps) {
  const [open, setOpen] = useState(false)
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('admin_soft_delete_quote', {
        p_quote_id: quoteId,
      })

      if (error) {
        throw new Error(error.message)
      }

      const result = data as { bookings_deleted?: number } | null
      return { bookingsDeleted: result?.bookings_deleted ?? 0 }
    },
    onSuccess: ({ bookingsDeleted }) => {
      setOpen(false)
      void queryClient.invalidateQueries({ queryKey: ['quotes-report'] })
      void queryClient.invalidateQueries({ queryKey: ['quotes-report-stats'] })
      void queryClient.invalidateQueries({ queryKey: ['bookings-report'] })
      toast({
        title: 'Quote deleted',
        description:
          bookingsDeleted > 0
            ? `The quote and ${bookingsDeleted} attached booking${bookingsDeleted === 1 ? '' : 's'} were deleted.`
            : 'The quote was deleted.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Delete failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      })
    },
  })

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-destructive"
          aria-label="Delete quote"
          // The table row navigates on click — keep the delete from doing both
          onClick={(e) => e.stopPropagation()}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this quote?</AlertDialogTitle>
          <AlertDialogDescription>
            {`This deletes the quote${customerName ? ` for ${customerName}` : ''} and every booking attached to it. They disappear from all dashboards, reports and customer links. `}
            {isConverted
              ? 'This quote has CONVERTED — the customer paid for the attached booking. Deleting removes that booking from the account. '
              : ''}
            Only platform staff can restore a deleted quote.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={mutation.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={mutation.isPending}
            onClick={(e) => {
              // Run the async delete ourselves; keep the dialog open until done
              e.preventDefault()
              mutation.mutate()
            }}
          >
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete quote
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
